import { FC, JSX, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom"
import { ColumnConfig, SortDirections, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import { BreadcrumbTemplateEnum } from "@app/shared/breadcrumbs/breadcrumbTemplates";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderLeft from "@app/shared/PageHeaderLeft";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import ClientLicenseService from "@app/admin/client-license/clientLicenseService";
import { LicenseClient } from "@app/admin/client-license/clientLicenseModels";

interface ClientLicenseTableData {
  [wafflesKeyColumnName]: string;
  licenseCount: JSX.Element;
  name: JSX.Element;
}

const clientLicenseColumnConfig: ColumnConfig = {
  licenseCount: {
    displayText: "Number of Licenses",
    minWidth: "175px",
    testId: "license-count",
  },
  name: {
    displayText: "Name",
    testId: "name",
  },
};

interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<ClientLicenseTableData>,
}

export const ClientLicenses: FC<React.PropsWithChildren<unknown>> = () => {

  const urlParams = new URLSearchParams(window.location.search);
  const clientLicenseService = new ClientLicenseService();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [clientLicenses, setClientLicenses] = useState<ClientLicenseTableData[]>([]);
  const [take] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [numDispRecLowerBound, setNumDispRecLowerBound] = useState(0);
  const [numDispRecUpperBound, setNumDispRecUpperBound] = useState(0);
  const [nameFilter, setNameFilter] = useState(urlParams.get("name") ?? "");
  const [numLicenseFilter, setNumLicenseFilter] = useState(urlParams.get("numLic") ?? "");
  const [isActiveOnlyFilter, setIsActiveOnlyFilter] = useState<boolean>(urlParams.get("activeOnly") === "true" ? true : false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [pageSettings, setPageSettings] = useState<PageSettings>(!!urlParams.get("pageSet") ? JSON.parse(urlParams.get("pageSet")) :
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null,
      },
    });

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => {
      setPageSettings({ ...pageSettings, skip: (pageNumber - 1) * take });
      window.scrollTo(0, 0);
    },
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: totalRecords,
  }

  useEffect(() => {
    fetchClientLicenses();
  }, [pageSettings]);

  const handleSort = (data: ClientLicenseTableData[], newSortedColumn: keyof ClientLicenseTableData, direction: SortDirections) => {
    setPageSettings({
      ...pageSettings,
      skip: 0,
      sortDetails: {
        sortDirection: direction,
        sortedColumn: newSortedColumn,
      }
    });
    return data;
  }

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  }

  const handleSearch = () => {
    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const clearFilter = () => {
    setNameFilter("");
    setNumLicenseFilter("");
    setIsActiveOnlyFilter(false);
    setIsFiltered(false);

    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const createWafflesTable = (data: LicenseClient[]) => {
    return data.map((rec) => {
      const name = (
        <>
          <QesIcon iconName={QesIcons.FileV4} description="File" />
          {!rec.isActive ?
            <>
              {rec.name} - <span className="text-danger">Deactivated</span>
            </> :
            <Link
              to={{
                pathname: `/admin/client-license/${rec.clientId}`,
                state: { clientName: rec.name }
              }}
            >
              {rec.name}
            </Link>
          }
        </>
      );

      const licenseCount = (
        <>
          {rec.licenseCount > 0 ?
            <span className={!rec.isActive ? "text-danger" : "text-primary"}>
              {rec.licenseCount}
            </span> :
            <span className="text-warning">
              No Software License Found
            </span>
          }
        </>
      );

      /* oxlint-disable sort-keys */
      return ({
        key: rec.clientId.toString(),
        name,
        licenseCount
      });
      /* oxlint-enable sort-keys */
    });
  }

  const fetchClientLicenses = async () => {
    setLoading(true);

    const licensesInfo = await clientLicenseService.getClientLicenseCounts(pageSettings.skip, take, pageSettings.sortDetails.sortDirection, pageSettings.sortDetails.sortedColumn, nameFilter, numLicenseFilter, isActiveOnlyFilter);

    setClientLicenses(createWafflesTable(licensesInfo.licenseClientList));
    setNumDispRecLowerBound(pageSettings.skip + 1);
    setNumDispRecUpperBound(licensesInfo.licenseClientList.length + pageSettings.skip);
    setTotalRecords(licensesInfo.totalCount);

    const searchParams = new URLSearchParams(window.location.search);

    setIsFiltered(!!(nameFilter || numLicenseFilter || isActiveOnlyFilter));
    if (pageSettings.skip > 0 || pageSettings.sortDetails.sortDirection !== SortDirections.None) {
      searchParams.set("pageSet", JSON.stringify(pageSettings));
    } else {
      searchParams.delete("pageSet");
    }
    if (!!nameFilter) {
      searchParams.set("name", nameFilter);
    } else {
      searchParams.delete("name");
    }
    if (!!numLicenseFilter) {
      searchParams.set("numLic", numLicenseFilter);
    } else {
      searchParams.delete("numLic");
    }
    if (!!isActiveOnlyFilter) {
      searchParams.set("activeOnly", isActiveOnlyFilter.toString());
    } else {
      searchParams.delete("activeOnly");
    }

    history.replace(history.location.pathname + "?" + searchParams.toString());

    setLoading(false);
  }

  return (
    <>
      {(
        <Page documentTitle="Client Licenses - Admin">
          <Breadcrumbs template={BreadcrumbTemplateEnum.ClientLicenses} items={[]} />
          <PageInner>
            <PageHeader title="Client Licenses" iconName={QesIcons.FileV4}>
              <PageHeaderLeft>
                <QESButton
                  qesTestId="client-licenses__refresh"
                  className="client-licenses__header-link-btn"
                  qesType="secondary"
                  onClick={() => { fetchClientLicenses(); }}
                >
                  <i className="fa fa-refresh me-1" />
                  Refresh
                </QESButton>
              </PageHeaderLeft>
            </PageHeader>
            <PageSection>
              <div className="client-licenses-filters mb-3 mt-5">
                <QESTextInput containerClassName="client-licenses-filters__input" label="Name" testId="client-licenses__filter-name" value={nameFilter} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setNameFilter(e.target.value)} />
                <QESTextInput containerClassName="client-licenses-filters__input" label="Number of Licenses" testId="client-licenses__filter-name" value={numLicenseFilter} onKeyUp={(e) => handleSearchKeyUp(e)} onChange={(e) => setNumLicenseFilter(e.target.value)} />
                <QESCheckbox
                  containerClassName="client-licenses-filters__checkbox me-4"
                  label="Show Active Only"
                  testId="client-licenses-filters__is-active-only"
                  checked={isActiveOnlyFilter}
                  onChange={(e) => setIsActiveOnlyFilter(e.target.checked)} />
                <QESButton qesType="primary" qesTestId="client-licenses__search-btn" onClick={handleSearch}>Search</QESButton>
                <QESButton qesType="secondary" qesTestId="client-licenses__clear-btn" onClick={clearFilter} disabled={!isFiltered}>Clear</QESButton>
              </div>
              {!loading ?
                <p>Displaying {totalRecords > 0 ? `${numDispRecLowerBound.toLocaleString("en-US")}-${numDispRecUpperBound.toLocaleString("en-US")} of ${totalRecords.toLocaleString("en-US")}` : "0"} clients</p>
                : null
              }
              <Waffles<ClientLicenseTableData>
                columns={clientLicenseColumnConfig}
                customSort={handleSort}
                data={clientLicenses}
                enablePagination={true}
                fullWidth={false}
                loading={loading}
                passThroughSorting={true}
                paginationProps={paginationProps}
                testId="client-licenses__grid-waffles-table"
              />
            </PageSection>
          </PageInner>
        </Page>
      )}
    </>
  );
}